import { EnumObject } from "@prequel/react";

import axios, {
  ApiResponse,
  ApiRoutes,
  ID_PLACEHOLDER,
  TestConnectionResponse,
  TestConnectionResult,
} from "@/api/client";
import { createResource, deleteResource, getResource, handleApiError, updateResource } from "@/api/helpers";
import ExistingSource, {
  ExistingSourceTestPayload,
  getExistingSshKey,
  PreparedSource,
  SourceType,
  SSHPublicKeyResponse,
} from "@/types/sources";
import { SourceForm } from "@/types/sources/form";

const getSource = async (sourceID: ExistingSource["id"]): Promise<ExistingSource> =>
  getResource<ExistingSource, { source: ExistingSource }>(
    `${ApiRoutes.SOURCES}/${sourceID}`,
    (response) => ({
      ...response.data.data.source,
      type: SourceType.ExistingSource,
      existing_ssh_key: getExistingSshKey(response.data.data.source),
    }),
    "Failed to fetch source.",
  );

const getSources: () => Promise<ExistingSource[]> = () =>
  axios
    .get(ApiRoutes.SOURCES)
    .then((response: ApiResponse<{ sources: ExistingSource[] }>) => {
      const sources: ExistingSource[] = response.data.data.sources.map((s: ExistingSource) => ({
        ...s,
        type: SourceType.ExistingSource,
        existing_ssh_key: getExistingSshKey(s),
      }));
      return sources;
    })
    .catch(handleApiError("Failed to fetch sources.", true));

const testNewSource: (s: PreparedSource) => Promise<TestConnectionResult> = (source) =>
  axios
    .post(ApiRoutes.TEST_SOURCE, { source })
    .then((response: ApiResponse<TestConnectionResponse>) => ({
      status: response.data.data?.status,
      message: response.data.message,
    }))
    .catch(handleApiError("Source connection test failed.", true));

const testExistingSource: (p: ExistingSourceTestPayload) => Promise<TestConnectionResult> = ({ sourceId, fields }) => {
  const url = ApiRoutes.TEST_EXISTING_SOURCE.replace(ID_PLACEHOLDER, sourceId);
  return axios
    .post(url, fields ? { source: fields } : undefined)
    .then((response: ApiResponse<TestConnectionResponse>) => ({
      status: response.data.data?.status,
      message: response.data.message,
    }))
    .catch(handleApiError("Source connection test failed.", true));
};

const postSource = (source: PreparedSource): Promise<ExistingSource> =>
  createResource<PreparedSource, ExistingSource>(
    ApiRoutes.SOURCES,
    source,
    "source",
    "source",
    "Source creation failed.",
  );

const patchSource = (sourceId: ExistingSource["id"], source: Partial<PreparedSource>): Promise<ExistingSource> =>
  updateResource<PreparedSource, ExistingSource>(
    `${ApiRoutes.SOURCES}/${sourceId}`,
    source,
    "source",
    "source",
    "Failed to update source.",
  );

const deleteSource = (sourceId: ExistingSource["id"]): Promise<void> =>
  deleteResource(`${ApiRoutes.SOURCES}/${sourceId}`, "Failed to delete source.");

const getSourceForm: (orgID?: string | undefined) => Promise<SourceForm> = (orgID) =>
  axios
    .get(ApiRoutes.PUBLIC_SOURCE_FORM, {
      params: {
        org_id: orgID,
      },
    })
    .then((response: ApiResponse<SourceForm>) => response.data.data)
    .catch(handleApiError("Failed to fetch source form."));

const getSourceVendors: () => Promise<EnumObject[]> = () =>
  axios
    .get(ApiRoutes.PUBLIC_SOURCE_VENDORS)
    .then((response: ApiResponse<EnumObject[]>) => response.data.data)
    .catch(handleApiError("Failed to fetch source vendors."));

const generateSourcePublicKey: (orgID?: string | undefined) => Promise<SSHPublicKeyResponse> = (orgID) =>
  axios
    .post(ApiRoutes.GENERATE_PUBLIC_KEY, null, {
      params: {
        org_id: orgID,
      },
    })
    .then((response: ApiResponse<SSHPublicKeyResponse>) => ({
      public_key: response.data.data.public_key,
      public_key_pkcs8: response.data.data.public_key_pkcs8,
      // fill in aliases for backward compatibility with SourceForm component
      ssh_rsa: response.data.data.public_key,
      pkcs8: response.data.data.public_key_pkcs8,
    }))
    .catch(handleApiError("Failed to generate public SSH key."));

const SourcesService = {
  getSource,
  getSources,
  testNewSource,
  postSource,
  patchSource,
  deleteSource,
  testExistingSource,
  getSourceForm,
  getSourceVendors,
  generateSourcePublicKey,
};
export default SourcesService;
